/* eslint-disable */
import React, { useState, useMemo } from 'react';
import { checkRecurringOverlap } from '../../../utils/leaderUtils';

/**
 * AbsenceCalendarView - A monthly team overview of all absences
 * 
 * Props:
 * - absences: Object { workerId: [Array of absences] }
 * - workers: Array of worker objects [{ id, name, ... }]
 * - onEntryClick: Function(workerId) - Opens the detail modal for that worker
 */
export default function AbsenceCalendarView({ absences = {}, workers = [], onEntryClick }) {
    const [currentDate, setCurrentDate] = useState(new Date());

    const STATUS_LEGEND = [
        { label: 'Urlaub', color: '#007aff' },
        { label: 'Krankheit', color: '#ff3b30' },
        { label: 'Termin', color: '#ff9500' },
        { label: 'Sonstiges', color: '#af52de' }
    ];

    // Calendar logic
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0(Sun) - 6(Sat)
    
    // Convert to 0(Mon) - 6(Sun) for easier grid
    const startOffset = (firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1);

    const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
    const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
    const goToToday = () => setCurrentDate(new Date());

    const monthName = currentDate.toLocaleString('de-DE', { month: 'long', year: 'numeric' });
    const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

    // Process absences into a flat list
    const allAbsencesList = useMemo(() => {
        const list = [];
        Object.keys(absences).forEach(wid => {
            const worker = workers.find(w => String(w.id) === String(wid));
            if (!worker) return;
            (absences[wid] || []).forEach(abs => {
                list.push({ ...abs, workerName: worker.name, workerId: wid });
            });
        });
        return list;
    }, [absences, workers]);

    // Calculate all 42 slots (6 weeks)
    const calendarSlots = useMemo(() => {
        const slots = [];
        const calendarStart = new Date(year, month, 1 - startOffset);
        for (let i = 0; i < 42; i++) {
            const date = new Date(calendarStart);
            date.setDate(calendarStart.getDate() + i);
            slots.push(date);
        }
        return slots;
    }, [year, month, startOffset]);

    // Helper to get status color
    const getStatusColor = (type) => {
        const types = { 
            'urlaub': '#007aff', 
            'krank': '#ff3b30', 
            'termin': '#ff9500', 
            'abwesend': '#af52de',
            'sonstiges': '#af52de',
            'dienstreise': '#ff9500'
        };
        return types[String(type).toLowerCase()] || '#94a3b8';
    };

    return (
        <div className="absence-calendar-container">
            {/* Header / Nav */}
            <div className="absence-calendar-header">
                <div className="calendar-title-group">
                    <h2>Abwesenheits-Kalender</h2>
                    <div className="calendar-legend" style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                        {STATUS_LEGEND.map(item => (
                            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color }} />
                                <span style={{ fontSize: '11px', color: '#86868b', fontWeight: '600' }}>{item.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="calendar-nav-group">
                    <button className="apple-btn-secondary icon-btn" onClick={prevMonth}>‹</button>
                    <button className="apple-btn-secondary" onClick={goToToday}>Heute</button>
                    <button className="apple-btn-secondary icon-btn" onClick={nextMonth}>›</button>
                </div>
            </div>

            <div className="calendar-main-wrapper">
                {/* Day Labels */}
                <div className="calendar-days-header">
                    {weekDays.map(d => <div key={d} className="calendar-day-label">{d}</div>)}
                </div>

                {/* Grid (Always 42 Slots) */}
                <div className="calendar-month-grid">
                    {calendarSlots.map((date, idx) => {
                        const d = date.getDate();
                        const m = date.getMonth();
                        const isOtherMonth = m !== month;
                        const isToday = new Date().toDateString() === date.toDateString();
                        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                        
                        // Create dummy assignment for overlap check
                        // Generate local date string (YYYY-MM-DD) to avoid UTC shift
                        const y = date.getFullYear();
                        const mon = String(date.getMonth() + 1).padStart(2, '0');
                        const dayNum = String(date.getDate()).padStart(2, '0');
                        const dateStr = `${y}-${mon}-${dayNum}`;

                        const curSlot = { 
                            start_datetime: `${dateStr}T00:00:00`, 
                            end_datetime: `${dateStr}T23:59:59`, 
                            all_day: true 
                        };

                        return (
                            <div key={idx} className={`calendar-day-cell ${isOtherMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`}>
                                <span className="day-number">{d}</span>
                                
                                <div className="day-absence-list">
                                    {allAbsencesList.filter(abs => checkRecurringOverlap(abs, curSlot)).map(abs => (
                                        <div 
                                            key={`${abs.id}-${dateStr}`} 
                                            className="absence-bar" 
                                            style={{ backgroundColor: getStatusColor(abs.absence_type) }}
                                            onClick={() => onEntryClick(abs.workerId, abs.id)}
                                            title={`${abs.workerName}: ${abs.absence_type}`}
                                        >
                                            <span className="worker-initials">{abs.workerName.split(' ').map(n => n[0]).join('')}</span>
                                            <span className="worker-full-name">{abs.workerName}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <style>{`
                .absence-calendar-container {
                    background: white;
                    border-radius: 28px;
                    padding: 20px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.05);
                    border: 1px solid rgba(0,0,0,0.02);
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    height: calc(100vh - 250px);
                    min-height: 500px;
                }

                .absence-calendar-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0 4px;
                }

                .calendar-title-group h2 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 800;
                    color: #1d1d1f;
                }

                .calendar-title-group p {
                    margin: 2px 0 0;
                    color: #86868b;
                    font-size: 13px;
                    font-weight: 500;
                }

                .calendar-nav-group {
                    display: flex;
                    gap: 8px;
                }

                .icon-btn {
                    padding: 0;
                    width: 36px;
                    height: 36px;
                    font-size: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .calendar-main-wrapper {
                    border: 1px solid #f2f2f7;
                    border-radius: 20px;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                }

                .calendar-days-header {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    background: #fbfbfd;
                    border-bottom: 1px solid #f2f2f7;
                }

                .calendar-day-label {
                    padding: 10px;
                    text-align: center;
                    font-size: 10px;
                    font-weight: 800;
                    color: #86868b;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .calendar-month-grid {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    grid-template-rows: repeat(6, 1fr);
                    flex: 1;
                }

                .calendar-day-cell {
                    border-right: 1px solid #f2f2f7;
                    border-bottom: 1px solid #f2f2f7;
                    padding: 6px;
                    position: relative;
                    transition: background 0.2s;
                    min-height: 0;
                }

                .calendar-day-cell:nth-child(7n) { border-right: none; }
                .calendar-day-cell:nth-last-child(-n+7) { border-bottom: none; }
                
                .calendar-day-cell.other-month {
                    background: #fcfcfd;
                }
                .calendar-day-cell.other-month .day-number {
                    opacity: 0.3;
                }
                .calendar-day-cell.other-month .day-absence-list {
                    opacity: 0.5;
                }

                .calendar-day-cell.weekend {
                    background: #fdfdfe;
                }

                .calendar-day-cell.today {
                    background: rgba(0, 122, 255, 0.02);
                }

                .day-number {
                    font-size: 11px;
                    font-weight: 700;
                    color: #86868b;
                    margin-bottom: 4px;
                    display: block;
                }

                .calendar-day-cell.today .day-number {
                    color: white;
                    background: #007aff;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .day-absence-list {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }

                .absence-bar {
                    font-size: 9px;
                    padding: 3px 6px;
                    border-radius: 4px;
                    color: white;
                    font-weight: 700;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    cursor: pointer;
                    transition: transform 0.2s;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }

                .absence-bar:hover {
                    transform: scale(1.02);
                    filter: brightness(1.1);
                    z-index: 10;
                }

                .worker-initials {
                    background: rgba(0,0,0,0.15);
                    padding: 1px 3px;
                    border-radius: 3px;
                    font-size: 7px;
                }

                @media (max-width: 1000px) {
                    .worker-full-name { display: none; }
                    .worker-initials { font-size: 9px; }
                }

                /* Container optimization for fitting screen */
                @media (max-height: 800px) {
                    .absence-calendar-container { padding: 12px; gap: 12px; }
                    .calendar-day-cell { padding: 4px; }
                }
            `}</style>
        </div>
    );
}
